import React, { useContext } from 'react';
import { Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import BreadcrumbsItem from './BreadcrumbsItem';
import { buildUrl } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { UserContext } from 'context';
import i18n from 'i18n-js';

export const isDefined = (v) =>
  v !== undefined && v !== null && v !== false && String(v).length > 0;

function formatBreadCrumb(str) {
  const exceptions = ['NFC'];
  return exceptions.includes(str)
    ? str
    : str.replace(/[-_]/g, ' ').replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
}

/**
 * Checks if the pathname meets certain conditions
 * for a redirection exception.
 * If it does, it modifies the paths by removing
 * a specific number from the path format.
 * @param {String} pathname Current window pathname
 * @param {Array} paths Array of paths to build the breadcrumbs
 */

const redirectionExceptions = (pathname, paths) => {
  const { orgType } = useContext(UserContext);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const hasFFBEnabled = sharingType === 'FFB';
  const currentOrgType = hasFFBEnabled ? (orgType === 'msServiceOrg' ? 'BO' : 'FO') : false;

  // Regular expression to match the format '/statement-entries/{number}/preview'
  const statement_entries_preview_regex = /\/statement-entries\/\d+\/preview\/?/;

  // Regular expression to match the format '/statement-entries/{number}'
  const statement_entries_pattern = /\/statement-entries\/\d+$/;

  // Regular expression to find a number
  const number_pattern = /\d+$/;

  // Check if the pathname matches the conditions for the redirection exception
  if (!(statement_entries_preview_regex.test(pathname) && currentOrgType === 'BO')) return;

  // Modify the paths based on the redirection exception
  paths.forEach((path, index) => {
    if (statement_entries_pattern.test(path.path))
      paths[index].path = path.path.replace(number_pattern, ``);
  });
};

/**
 * Gets the paths for building the breadcrumbs based on the current pathname.
 * @param {String} pathname Current window pathname
 * @param {String|null} rootName Root name for the breadcrumbs (optional)
 * @returns {Array} Array of paths for the breadcrumbs
 */
const getPaths = (pathname, rootName = null) => {
  const org = window.location.pathname.split('/')[1] || sessionStorage.getItem('org_slug');
  const unauthorized = window.location.pathname.split('/')[2];
  const paths = [
    {
      name: isDefined(rootName)
        ? typeof rootName === 'string'
          ? rootName
          : '/'
        : i18n.t('sidebar.dashboard'),
      path: `/${org}`,
    },
  ];

  if (unauthorized === '404') {
    return [];
  }

  if (pathname === '/' || pathname === `/${org}` || pathname === `/${org}/dashboard`) return paths;

  pathname
    .split('/')
    .filter((e, i) => ![1].includes(i))
    .reduce((prev, curr) => {
      const currPath = `${prev}/${curr}`;

      paths.push({
        name: formatBreadCrumb(curr),
        path: buildUrl(currPath),
      });

      return currPath;
    });
  redirectionExceptions(pathname, paths);
  return paths;
};

const Breadcrumbs = (props) => {
  const { location, mappedRoutes, WrapperComponent, rootName } = props;
  const path = location.pathname;

  if (isDefined(rootName))
    mappedRoutes['/'] = (url, match) =>
      typeof rootName === 'function' ? rootName(path, match) : rootName;

  const paths = getPaths(path);

  return (
    <WrapperComponent>
      {paths.map((p, idx) => (
        <Route
          key={idx}
          path={p.path}
          render={(rest) => (
            <BreadcrumbsItem
              parentProps={props}
              mappedRoutes={mappedRoutes}
              name={p.name}
              {...rest}
            />
          )}
        />
      ))}
    </WrapperComponent>
  );
};

Breadcrumbs.propTypes = {
  location: PropTypes.shape({}).isRequired,
};

export default Breadcrumbs;
